import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import api from '../utils/api';
import '../styles/global.css';

export default function Home() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const { client, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/sessions')
      .then(res => setSessions(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const formatDate = (date) => new Date(date).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });

  const gradients = [
    'linear-gradient(135deg, var(--teal-light), var(--teal))',
    'linear-gradient(135deg, var(--coral-light), var(--coral))',
    'linear-gradient(135deg, var(--teal-light), var(--teal-dark))',
  ];

  const isNew = (session) => {
    const created = new Date(session.created_at);
    const now = new Date();
    return (now - created) < 7 * 24 * 60 * 60 * 1000;
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <header className="app-header">
        <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '20px', color: 'var(--teal)' }}>
          BUMP <span style={{ color: '#F68F7C' }}>&#x2665;&#xFE0E;</span> BABY IMAGING
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '36px', height: '36px', background: 'var(--teal)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 800, fontSize: '14px', fontFamily: 'Nunito, sans-serif' }}>
            {client?.firstName?.[0]}{client?.lastName?.[0]}
          </div>
          <button onClick={() => navigate('/settings')} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: '100px', padding: '6px 14px', fontSize: '12px', fontWeight: 700, color: 'var(--text-soft)', cursor: 'pointer', fontFamily: 'Nunito, sans-serif' }}>Settings</button>
          <button onClick={logout} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: '100px', padding: '6px 14px', fontSize: '12px', fontWeight: 700, color: 'var(--text-soft)', cursor: 'pointer', fontFamily: 'Nunito, sans-serif' }}>Log out</button>
        </div>
      </header>

      {/* Content */}
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '28px 24px', width: '100%', flex: 1 }}>
        <div style={{ marginBottom: '28px' }}>
          <h2 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '26px', color: 'var(--teal-dark)' }}>
            Hello, {client?.firstName} <span style={{ color: '#F68F7C' }}>&#x2665;&#xFE0E;</span>
          </h2>
          <p style={{ fontSize: '14px', color: 'var(--text-soft)', marginTop: '4px' }}>Your memories from Bump Baby Imaging are here whenever you need them.</p>
        </div>

        {sessions.some(s => isNew(s)) && (
          <div style={{ background: 'var(--coral-light)', border: '1px solid #f5c4b5', borderRadius: '14px', padding: '16px 20px', marginBottom: '28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
            <p style={{ fontSize: '14px', color: 'var(--coral-dark)', fontWeight: 600 }}>
              <strong>New session added!</strong> Share your gallery with family and friends.
            </p>
            <button className="btn-teal" onClick={() => navigate(`/sessions/${sessions[0]?.id}`)}>View Session</button>
          </div>
        )}

        <div className="section-label">Your Sessions</div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-soft)' }}>Loading your sessions...</div>
        ) : sessions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-soft)', background: 'white', borderRadius: '18px', border: '1px solid var(--border)' }}>
            <p style={{ fontSize: '16px', marginBottom: '8px' }}>No sessions yet</p>
            <p style={{ fontSize: '14px' }}>Your gallery will appear here after your session.</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '20px' }}>
            {sessions.map((session, i) => (
              <div
                key={session.id}
                onClick={() => navigate(`/sessions/${session.id}`)}
                style={{ background: 'white', borderRadius: '18px', overflow: 'hidden', border: '1px solid var(--border)', cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s', boxShadow: '0 2px 12px rgba(83,147,144,0.08)' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(83,147,144,0.18)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(83,147,144,0.08)'; }}
              >
                <div style={{ height: '140px', background: gradients[i % 3], position: 'relative' }}>
                  {isNew(session) && (
                    <div style={{ position: 'absolute', top: '12px', right: '12px', background: 'var(--coral)', color: 'white', fontSize: '11px', fontWeight: 800, fontFamily: 'Nunito, sans-serif', padding: '3px 10px', borderRadius: '100px' }}>NEW</div>
                  )}
                </div>
                <div style={{ padding: '16px 18px' }}>
                  <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '16px', color: 'var(--teal-dark)' }}>{session.session_type}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-soft)', marginTop: '2px' }}>
                    {formatDate(session.session_date)}
                    {session.expires_at && <span style={{ color: 'var(--coral)', marginLeft: '8px' }}>· Expires {formatDate(session.expires_at)}</span>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '12px' }}>
                    <span style={{ fontSize: '11px', fontWeight: 700, padding: '3px 9px', borderRadius: '100px', background: 'var(--teal-light)', color: 'var(--teal)' }}>
                      {session.video_count > 0 ? '1 video · ' : ''}{session.image_count || 0} images
                    </span>
                    <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--coral)', fontFamily: 'Nunito, sans-serif' }}>View →</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <footer className="app-footer">
        <p>Made with <span className="heart">&#x2665;&#xFE0E;</span> by <strong>Bump Baby Imaging</strong></p>
        <p style={{ marginTop: '4px' }}>info@bumpbabyimaging.com.au</p>
      </footer>
    </div>
  );
}
